import { useState, useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

export interface AuthState {
  loading: boolean;
  user: User | null;
  sessionExpired: boolean;
  error: string | null;
}

export function useAuthState(): AuthState & { signOut: () => Promise<void> } {
  const [state, setState] = useState<AuthState>({
    loading: true,
    user: null,
    sessionExpired: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const { data: { session }, error: sessionErr } = await supabase.auth.getSession();

        if (!cancelled) {
          if (sessionErr) {
            setState({ loading: false, user: null, sessionExpired: false, error: sessionErr.message });
            return;
          }
          setState({
            loading: false,
            user: session?.user || null,
            sessionExpired: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            user: null,
            sessionExpired: false,
            error: err instanceof Error ? err.message : 'Failed to check session',
          });
        }
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
        setState({ loading: false, user: null, sessionExpired: event === 'TOKEN_REFRESHED', error: null });
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setState({ loading: false, user: session?.user || null, sessionExpired: false, error: null });
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      // onAuthStateChange 會收到 SIGNED_OUT 事件並同步所有 useAuthState() 實例，
      // 不需要手動清 state 或重新整理頁面。
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : '登出失敗',
      }));
    }
  };

  return { ...state, signOut };
}

interface AuthGuardProps {
  children: (auth: AuthState & { signOut: () => Promise<void> }) => ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const auth = useAuthState();

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">載入中……</p>
        </div>
      </div>
    );
  }

  return <>{children(auth)}</>;
}